<template>
  <div class="surgery-report-container  py-4">
    <!-- Header Section -->
    <div class="d-flex flex-wrap justify-content-between align-items-center mb-3">
      <h3 class="report-title">
        <i class="el-icon-s-data mr-2"></i>
        Datewise Report
      </h3>
    </div>

    <!-- Filter Section -->
    <div class="row mb-4 align-items-end">
      <div class="col-auto">
        <span>Select Date(s)</span>
        <br />
        <el-select v-model="dateFilterOption">
          <el-option :value="0" :label="'All'">All</el-option>
          <el-option :value="1" :label="'Custom Date'">Custom Date</el-option>
        </el-select>
      </div>
      <div v-if="dateFilterOption" class="col-auto">
        <span>Select Date Range</span>
        <v-md-date-range-picker
          :presets="dateRangePresets"
          :start-date="dateRange.start_date"
          :end-date="dateRange.end_date"
          @change="handleChange"
          style="width:100% !important"
        ></v-md-date-range-picker>
      </div>
      <div class="col mt-2">
        <el-button type="primary" @click="getSurgeonReport"
          >Get Report</el-button
        >
      </div>
    </div>

    <!-- 🟦 Operations Section -->
    <el-card shadow="hover" class="section-card mb-4">
      <div class="section-header mb-3">
        <h6><i class="el-icon-s-operation mr-2"></i>Operations Summary</h6>
      </div>

      <div v-if="operationsRecords.length" class="row g-3">
        <div
          v-for="(item, index) in operationsRecords"
          :key="'operation-' + index"
          class="col-md-3 col-sm-12 my-2"
        >
          <div class="data-card border rounded shadow-sm p-3 bg-white h-100">
            <div class="card-header d-flex justify-content-between align-items-center mb-2">
              <h5 class="mb-0">{{ (item.date) }}</h5>
            </div>

            <div class="data-content">
              <div class="data-item">
                <span class="label">Spay (Female):</span>
                <span class="value">{{ item.spay_female }}</span>
              </div>
              <div class="data-item">
                <span class="label">Castration (Male):</span>
                <span class="value">{{ item.castration_male }}</span>
              </div>
              <div class="data-item">
                <span class="label">Total Surgeries:</span>
                <span class="value">{{ item.total_surgeries }}</span>
              </div>
              <div class="data-item">
                <span class="label">Average Time (mins):</span>
                <span class="value">{{ item.average_time_mins }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div v-else class="text-center py-4 text-muted">
        <i class="el-icon-document-remove" style="font-size: 36px"></i>
        <p class="mt-2">No operations data found.</p>
      </div>
    </el-card>

    <!-- 🟩 Catch & Release Section -->
    <el-card shadow="hover" class="section-card">
      <div class="section-header mb-3">
        <h6><i class="el-icon-pie-chart mr-2"></i>Catch & Release Summary</h6>
      </div>

      <div v-if="catchReleaseRecords.length" class="row g-3">
        <div
          v-for="(item, index) in catchReleaseRecords"
          :key="'catch-' + index"
          class="col-md-3 col-sm-12 my-2"
        >
          <div class="data-card border rounded shadow-sm p-3 bg-white h-100">
            <div class="card-header">
              <h5 class="mb-0">{{ (item.date) }}</h5>
            </div>

            <div class="data-content">
              <div class="data-item">
                <span class="label">Caught:</span>
                <span class="value">{{ item.caught }}</span>
              </div>
              <div class="data-item">
                <span class="label">Released:</span>
                <span class="value">{{ item.released }}</span>
              </div>
              <div class="data-item">
                <span class="label">Pre-Op Died:</span>
                <span class="value">{{ item.pre_op_died }}</span>
              </div>
              <div class="data-item">
                <span class="label">Post-Op Died:</span>
                <span class="value">{{ item.post_op_died }}</span>
              </div>
              <div class="data-item">
                <span class="label">Euthanasia:</span>
                <span class="value">{{ item.euthanasia }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div v-else class="text-center py-4 text-muted">
        <i class="el-icon-document-remove" style="font-size: 36px"></i>
        <p class="mt-2">No catch & release data found.</p>
      </div>
    </el-card>

  </div>
</template>

<script>
import { Card, Button, Tag, Message, DatePicker } from 'element-ui';
import apiService from '../../../../services/api.service';
import dateRangeDefaultPresetsMixin from '../../../../mixins/DateRangeDefaultPresetsMixin';

export default {
  components: {
    [Button.name]: Button,
    [DatePicker.name]: DatePicker,
    [Tag.name]: Tag,
    [Card.name]: Card,
  },
  props: {
    projectId: {
      type: String | Number,
      required: true
    }
  },
  data() {
    const today = new Date();
    const firstDay = new Date(today.getFullYear(), today.getMonth(), 1);
    const lastDay = new Date(today.getFullYear(), today.getMonth() + 1, 0);
    return {
      catchReleaseRecords: [],
      operationsRecords: [],
      dateRange: {
        start_date: firstDay.toLocaleDateString("en-US"),
        end_date: lastDay.toLocaleDateString("en-US"),
      },
      dateFilterOption: 0,
      dateRangePresets: [],
    };
  },
  mixins: [dateRangeDefaultPresetsMixin],
  methods: {
    async getSurgeonReport() {
      const vm = this;
      const loading = vm.$loading({
        lock: true,
        text: 'Loading statistics...',
        spinner: 'el-icon-loading',
        background: 'rgba(0, 0, 0, 0.7)'
      });
      try {
        const payload = this.dateFilterOption ? this.dateRange : {};
        const apiResponse = await apiService.getPmDateWiseReport(vm.projectId, payload);
        const { response } = apiResponse;
        if (response.error && response.error.error_code === 0) {
          vm.catchReleaseRecords = response.data.catch_release;
          vm.operationsRecords = response.data.operations;
        } else {
          Message.error('Failed to load surgery statistics');
        }
      } catch (error) {
        console.error('Error fetching surgeon statistics:', error);
        Message.error('An error occurred while fetching surgeon statistics');
      } finally {
        loading.close();
      }
    },
    handleChange(values) {
      this.dateRange.start_date = this.formatDate(values[0]._d);
      this.dateRange.end_date = this.formatDate(values[1]._d);
    },
    formatDate(date) {
      const d = new Date(date);
      const year = d.getFullYear();
      const month = String(d.getMonth() + 1).padStart(2, "0");
      const day = String(d.getDate()).padStart(2, "0");
      return `${year}-${month}-${day}`;
    }
  },
  mounted() {
    this.getSurgeonReport();
    this.dateRangePresets = this.getDateRangePresets();
  }
};
</script>

<style scoped>
/* Container */
.daily-report-container {
  background: #f9fafc;
  min-height: 100vh;
}

.report-title {
  color: #2c3e50;
  font-weight: 600;
  margin-bottom: 5px;
  display: flex;
  align-items: center;
}

/* Section */
.section-card {
  border-radius: 12px;
  background: #fff;
  border: none;
}

.section-header h4 {
  font-size: 18px;
  font-weight: 600;
  color: #333;
  display: flex;
  align-items: center;
}

/* Cards */
.data-card {
  transition: all 0.3s ease;
  border: 1px solid #eef1f6;
  border-radius: 10px;
}

.data-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 6px 12px rgba(0, 0, 0, 0.08);
}

/* Inside card */
.card-header {
  padding: 10px !important;
}
.card-header h5 {
  font-size: 16px;
  font-weight: 600;
  color: #333;
}

.data-content {
  margin-top: 8px;
}

.data-item {
  display: flex;
  justify-content: space-between;
  font-size: 14px;
  margin-bottom: 6px;
}

.data-item .label {
  font-weight: 500;
  color: #666;
}

.data-item .value {
  font-weight: 600;
  color: #222;
}

/* Responsive */
@media (max-width: 768px) {
  .data-item {
    font-size: 13px;
  }
  .card-header h5 {
    font-size: 14px;
  }
}

@media (max-width: 480px) {
  .daily-report-container {
    padding: 10px;
  }
  .section-header h4 {
    font-size: 16px;
  }
  .data-card {
    padding: 12px;
  }
}
</style>